import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const DocumentsContext = createContext();

export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocuments must be used within a DocumentsProvider');
  }
  return context;
};

export const DocumentsProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byType: {},
    byStatus: {},
    totalAmount: 0,
    thisMonth: 0,
    lastMonth: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Récupérer tous les documents
  const fetchDocuments = async (filters = {}) => {
    // Prevent multiple rapid calls (debounce)
    const now = Date.now();
    if (now - lastFetchTime < 1000) { // 1 second debounce
      return { documents, total: documents.length, hasMore: false };
    }
    setLastFetchTime(now);
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      
      const response = await axios.get(`/api/documents?${params.toString()}`);
      
      if (filters.offset > 0) {
        // Si c'est une pagination, ajouter aux documents existants
        setDocuments(prev => [...prev, ...response.data.documents]);
      } else {
        // Sinon, remplacer les documents
        setDocuments(response.data.documents);
      }
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des documents:', error);
      setError('Erreur lors de la récupération des documents');
      
      // Only show toast if it's not a rate limit error to prevent spam
      if (error.response?.status !== 429) {
        toast.error('Erreur lors de la récupération des documents');
      }
      
      return { documents: [], total: 0, hasMore: false };
    } finally {
      setLoading(false);
    }
  };

  // Récupérer un document par ID
  const fetchDocument = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/documents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du document:', error);
      setError('Document non trouvé');
      toast.error('Document non trouvé');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Créer un nouveau document
  const createDocument = async (documentData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Generate a local ID for the document if none is provided
      if (!documentData.id) {
        documentData.id = `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      }
      
      // Try to create document via API
      let createdDocument;
      try {
        const response = await axios.post('/api/documents', documentData);
        createdDocument = response.data.document;
      } catch (apiError) {
        console.warn('API error when creating document, using local data instead:', apiError);
        // Create a local document if API fails
        createdDocument = {
          ...documentData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: documentData.status || 'draft'
        };
      }
      
      // Always add the document to local state, whether from API or local data
      setDocuments(prev => [createdDocument, ...prev]);
      
      // Try to update statistics
      try {
        await fetchStats();
      } catch (statsError) {
        console.warn('Error updating stats after document creation:', statsError);
      }
      
      toast.success('Document créé avec succès');
      return createdDocument;
    } catch (error) {
      console.error('Erreur lors de la création du document:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la création du document';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour un document
  const updateDocument = async (id, documentData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.put(`/api/documents/${id}`, documentData);
      
      // Mettre à jour le document dans la liste
      setDocuments(prev => 
        prev.map(doc => doc.id === id ? response.data.document : doc)
      );
      
      // Mettre à jour les statistiques
      await fetchStats();
      
      toast.success('Document mis à jour avec succès');
      return response.data.document;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du document:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la mise à jour du document';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un document
  const deleteDocument = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.delete(`/api/documents/${id}`);
      
      // Supprimer le document de la liste
      setDocuments(prev => prev.filter(doc => doc.id !== id));
      
      // Mettre à jour les statistiques
      await fetchStats();
      
      toast.success('Document supprimé avec succès');
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la suppression du document';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour le statut d'un document
  const updateDocumentStatus = async (id, status) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.patch(`/api/documents/${id}/status`, { status });
      
      // Mettre à jour le document dans la liste
      setDocuments(prev => 
        prev.map(doc => doc.id === id ? response.data.document : doc)
      );
      
      // Mettre à jour les statistiques
      await fetchStats();
      
      toast.success('Statut mis à jour avec succès');
      return response.data.document;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la mise à jour du statut';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les statistiques
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/documents/stats/overview');
      setStats(response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      
      // Don't show error toasts for stats failures to avoid spam
      if (error.response?.status !== 429) {
        console.warn('Failed to fetch stats, using cached data');
      }
      
      return stats;
    }
  };

  // Filtrer les documents localement
  const filterDocuments = (filters) => {
    let filtered = [...documents];
    
    if (filters.type) {
      filtered = filtered.filter(doc => doc.type === filters.type);
    }
    
    if (filters.status) {
      filtered = filtered.filter(doc => doc.status === filters.status);
    }
    
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.number?.toLowerCase().includes(searchTerm) ||
        doc.contactName?.toLowerCase().includes(searchTerm) ||
        doc.notes?.toLowerCase().includes(searchTerm)
      );
    }
    
    if (filters.dateFrom) {
      filtered = filtered.filter(doc => new Date(doc.date) >= new Date(filters.dateFrom));
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(doc => new Date(doc.date) <= new Date(filters.dateTo));
    }
    
    return filtered;
  };

  // Calculer les métriques
  const calculateMetrics = () => {
    const totalDocuments = documents.length;
    const totalAmount = documents.reduce((sum, doc) => sum + (doc.total || 0), 0);
    
    const pendingDocuments = documents.filter(doc => 
      doc.status === 'pending' || doc.status === 'draft'
    ).length;
    
    const thisMonth = new Date();
    thisMonth.setDate(1);
    thisMonth.setHours(0, 0, 0, 0);
    
    const thisMonthDocuments = documents.filter(doc => 
      new Date(doc.createdAt) >= thisMonth
    ).length;
    
    return {
      totalDocuments,
      totalAmount,
      pendingDocuments,
      thisMonthDocuments
    };
  };

  // Charger les données initiales
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchDocuments();
        await fetchStats();
      } catch (error) {
        console.error('Error loading initial documents data:', error);
        // Don't re-throw error to prevent infinite loops
      }
    };
    
    loadInitialData();
  }, []); // Empty dependency array to run only once

  const value = {
    // État
    documents,
    stats,
    loading,
    error,
    
    // Actions
    fetchDocuments,
    fetchDocument,
    createDocument,
    updateDocument,
    deleteDocument,
    updateDocumentStatus,
    fetchStats,
    
    // Utilitaires
    filterDocuments,
    calculateMetrics
  };

  return (
    <DocumentsContext.Provider value={value}>
      {children}
    </DocumentsContext.Provider>
  );
};

export default DocumentsContext;